/**
 * The findings list's tree, shared by every surface that lists findings the
 * way the Research workspace does: its toggle button (the document icon with
 * the count badge), the Source → Section → Reference rows, and the list's
 * header and empty state. UnifiedFindingsPanel (Research, reports, builds)
 * and Chat's findings list (components/chat/ChatFindings.tsx) both render
 * these, so a finding looks the same wherever it is listed.
 */

import React from 'react';
import { FileText, AlertTriangle, ChevronRight, ChevronDown, Scale, Trash2, X } from 'lucide-react';
import { cleanSourceText } from '../../utils/contentFormatter';
import type { DictUploadedFile, LinkedOutputReference } from '../../types/workspaceState';
import { detectFindingSourceType, isMeaningful } from '../../utils/findingSourceType';

/** Finding data from the project workspace */
export interface Finding {
  finding_id: string;
  source_document_celex?: string;
  source_document_name?: string;
  source_reference_hierarchy?: string;
  source_reference?: string;
  source_text?: string;
  source_snippet?: string;
  extracted_at?: string;
  tool_used?: string;
  document_version?: string;
  source_service?: string;
  source_url?: string;
  source_type?: string;
  document_type?: string;
  document_full_title?: string;
  article_title?: string;
  legal_status?: string;
  chunk_id?: string;
  page_number?: number;
  paragraph_id?: string;
  bbox?: Record<string, any>;
  authority_abbrev?: string;
  authority_name_en?: string;
  lifecycle_stage?: string;
  reference_type?: string;
  pdf_url?: string;
  page_url?: string;
  document_status?: string;
  document_category?: string;
  publication_date?: string;
  regulatory_framework?: string[];
  legal_basis?: string[];
  applies_to?: string[];
  effective_date?: string;
  parent_reference?: string;
  replaces?: string;
  amends?: string[];
  compliance_deadline?: string;
  cross_references?: Array<{ direction: string; article: string; celex: string; document_name: string }>;
  concepts?: string[];
  linked_qa?: Array<{ ref: string; target: string; date?: string; title?: string }>;
  qa_topic?: string;
  qa_legal_act?: string;
  qa_status?: string;
  qa_submitted_date?: string;
  qa_clarifies?: string[];
  definition_sources?: Array<{ ref: string; celex: string; short_name: string }>;
  concept_relationships?: Record<string, string[]>;
  // Workspace-input provenance (legacy imported facts)
  category?: string;
  source_workspace_id?: string;
}

// =============================================================================
// TREE MODEL — Source → Section → Reference
// =============================================================================

export type LeafKind = 'finding' | 'online' | 'upload' | 'linked';

export interface RefLeaf {
  id: string;
  kind: LeafKind;
  title: string;
  summary?: string;
  badge?: string | null;
  nonFinal?: boolean;
  finding?: Finding;
  onlineId?: string;
  file?: DictUploadedFile;
  linkedWsId?: string;
  linkedWsName?: string;
  linkedRef?: LinkedOutputReference;
}

export interface TreeSection {
  id: string;
  title: string;
  subtitle?: string;
  statusBadge?: string | null;     // e.g. document status "DRAFT"
  leaves: RefLeaf[];
  pendingNote?: string;            // shown when a section legitimately has no refs yet
  deletableFile?: DictUploadedFile;
}

export interface TreeSource {
  id: 'regulatory' | 'workspace' | 'online' | 'uploaded';
  label: string;
  icon: React.ReactNode;
  sections: TreeSection[];
  count: number;
}

// =============================================================================
// HELPERS
// =============================================================================

/** Natural sort for article references */
export function naturalSortKey(ref: string): string {
  return ref.replace(/(\d+)/g, (_, n) => n.padStart(6, '0'));
}

const DOC_CATEGORY_LABELS: Record<string, string> = {
  'final guideline': 'GL', 'final guidelines': 'GL', 'guideline': 'GL', 'guidelines': 'GL',
  'regulatory technical standards': 'RTS', 'regulatory technical standard': 'RTS', 'draft rts': 'Draft RTS',
  'implementing technical standards': 'ITS', 'implementing technical standard': 'ITS', 'draft its': 'Draft ITS',
  'consultation paper': 'CP', 'recommendation': 'REC', 'opinion': 'Opinion', 'report': 'Report',
};

export function isNonFinal(finding: Finding): boolean {
  if (!finding.document_status) return false;
  const status = finding.document_status.toLowerCase();
  return status === 'consultation' || status === 'draft';
}

/** Regulatory document grouping key (pdf/eurlex/qa/unknown findings only). */
export function getRegulatoryGroupKey(finding: Finding): string {
  const sourceType = detectFindingSourceType(finding);
  if (sourceType === 'pdf' && finding.source_document_celex && isMeaningful(finding.source_document_celex)) {
    return finding.source_document_celex;
  }
  if (sourceType === 'pdf') {
    return finding.source_document_name || finding.source_document_celex || '__unknown__';
  }
  if (finding.parent_reference) return finding.parent_reference;
  return finding.source_document_name || finding.source_document_celex || '__unknown__';
}

export function getRegulatoryGroupLabel(key: string, findings: Finding[]): string {
  if (key === '__unknown__') return 'Other sources';
  const category = findings[0]?.document_category?.toLowerCase();
  const shortLabel = category ? DOC_CATEGORY_LABELS[category] : null;
  return shortLabel ? `${key} (${shortLabel})` : key;
}

export function findingLeafTitle(finding: Finding): string {
  const sourceType = detectFindingSourceType(finding);
  const ref = finding.source_reference || '';
  if (sourceType === 'qa') return ref ? `Q&A ${ref}` : 'EBA Q&A';
  return ref || finding.source_document_name || finding.finding_id;
}

export function findingLeafSummary(finding: Finding): string {
  if (finding.article_title) return finding.article_title;
  const text = finding.source_snippet || finding.source_text;
  return text ? cleanSourceText(text).slice(0, 100) : '';
}

export const LINKED_KIND_LABELS: Record<string, string> = {
  report: 'Report',
  spec_requirement: 'Spec',
  delivered_artifact: 'Artifact',
  doc_file: 'Doc',
};

// =============================================================================
// TOGGLE BUTTON (exported for parent components to render)
// =============================================================================

export function FindingsToggleButton({
  findingsCount, onToggle, position = 'absolute top-16 right-4 z-20', label, expanded,
}: {
  findingsCount: number; onToggle: () => void;
  /** Where the button sits. Default: floating over the workspace's top right.
   *  A header that lays it out itself passes `relative`. */
  position?: string;
  /** Accessible name, when the badge alone would not say what it counts. */
  label?: string;
  /** For a button that also closes the list it opens (aria-expanded). */
  expanded?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`${position} p-2 rounded-lg shadow-lg border transition-all bg-surface/90 backdrop-blur-md text-fg-secondary border-border-default/50 hover:bg-surface`}
      title={`Open findings panel (${findingsCount})`}
      aria-label={label}
      aria-expanded={expanded}
    >
      <FileText size={20} aria-hidden="true" />
      {findingsCount > 0 && (
        <span aria-hidden={label ? true : undefined} className="absolute -top-1.5 -right-1.5 bg-accent text-white text-xs min-w-[20px] h-5 rounded-full flex items-center justify-center px-1 font-medium">
          {findingsCount}
        </span>
      )}
    </button>
  );
}

// =============================================================================
// REFERENCE ROW (leaf)
// =============================================================================

export function ReferenceRow({
  leaf, isSelected, onClick, onDelete, isDeleting,
}: {
  leaf: RefLeaf;
  isSelected: boolean;
  onClick: () => void;
  onDelete?: () => void;
  isDeleting?: boolean;
}) {
  const dataAttr =
    leaf.kind === 'finding' ? { 'data-finding-id': leaf.finding?.finding_id }
    : leaf.kind === 'online' ? { 'data-online-id': leaf.onlineId }
    : {};

  return (
    <div
      {...dataAttr}
      className={`group/leaf flex items-start gap-1 rounded transition-all ${
        isSelected
          ? 'bg-link-bg border border-link'
          : leaf.nonFinal
            ? 'border border-warning/40 hover:bg-warning-bg/40'
            : 'border border-transparent hover:bg-bg-warm'
      }`}
    >
      <button
        type="button"
        onClick={onClick}
        className="min-w-0 flex-1 text-left px-1.5 py-1 rounded"
        title={leaf.summary ? `${leaf.title} — ${leaf.summary}` : leaf.title}
      >
        <div className="flex items-center gap-1 min-w-0">
          {leaf.nonFinal && <AlertTriangle size={9} className="text-warning flex-shrink-0" />}
          <p className="font-medium text-fg truncate leading-tight text-[11px]">{leaf.title}</p>
          {leaf.badge && (
            <span className="ml-auto flex-shrink-0 text-[8px] uppercase tracking-wide px-1 py-0.5 rounded bg-bg-warm text-fg-tertiary">
              {leaf.badge}
            </span>
          )}
        </div>
        {leaf.summary && (
          <p className="text-[10px] text-fg-tertiary truncate leading-tight mt-0.5">{leaf.summary}</p>
        )}
      </button>
      {onDelete && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          disabled={isDeleting}
          className="p-1 rounded text-fg-tertiary hover:text-danger hover:bg-danger-bg disabled:opacity-50 opacity-0 group-hover/leaf:opacity-100 focus:opacity-100 transition self-center"
          title="Delete uploaded document"
          aria-label={`Delete ${leaf.title}`}
        >
          {isDeleting
            ? <span className="block w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
            : <Trash2 size={12} strokeWidth={1.6} />}
        </button>
      )}
    </div>
  );
}

// =============================================================================
// SECTION (level 2)
// =============================================================================

export function SectionGroup({
  section, collapsed, onToggle, selectedLeafId, selectedOnlineId, onLeafClick, onLeafDelete, deletingUploadId,
}: {
  section: TreeSection;
  collapsed: boolean;
  onToggle: () => void;
  selectedLeafId: string | null;
  selectedOnlineId: string | null;
  onLeafClick: (leaf: RefLeaf) => void;
  onLeafDelete: (file: DictUploadedFile) => void;
  deletingUploadId: string | null;
}) {
  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-1 px-1 py-0.5 rounded hover:bg-bg-warm transition-colors text-left"
      >
        {collapsed
          ? <ChevronRight size={11} className="text-fg-tertiary flex-shrink-0" />
          : <ChevronDown size={11} className="text-fg-tertiary flex-shrink-0" />}
        <span className="font-semibold text-fg-secondary truncate text-[10px]">{section.title}</span>
        {section.statusBadge && (
          <span className="text-[8px] px-1 py-0.5 rounded bg-warning-bg text-warning font-medium uppercase flex-shrink-0">
            {section.statusBadge}
          </span>
        )}
        <span className="text-[9px] text-fg-tertiary flex-shrink-0">{section.leaves.length || ''}</span>
        {section.deletableFile && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onLeafDelete(section.deletableFile!); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onLeafDelete(section.deletableFile!); } }}
            className="ml-auto p-0.5 rounded text-fg-tertiary hover:text-danger hover:bg-danger-bg opacity-0 group-hover:opacity-100 transition flex-shrink-0"
            title="Delete uploaded document"
          >
            {deletingUploadId === section.deletableFile.upload_id
              ? <span className="block w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
              : <Trash2 size={11} strokeWidth={1.6} />}
          </span>
        )}
      </button>
      {section.subtitle && !collapsed && (
        <p className="pl-4 text-[9px] text-fg-tertiary italic truncate leading-tight mb-0.5">{section.subtitle}</p>
      )}
      {!collapsed && (
        <div className="pl-3 ml-1 border-l border-border-default/60 space-y-0.5">
          {section.leaves.length === 0 ? (
            <p className="px-1.5 py-1 text-[10px] text-fg-tertiary italic">
              {section.pendingNote || 'Nothing here yet'}
            </p>
          ) : (
            section.leaves.map((leaf) => (
              <ReferenceRow
                key={leaf.id}
                leaf={leaf}
                isSelected={
                  selectedLeafId === leaf.id ||
                  (leaf.kind === 'online' && !!selectedOnlineId && leaf.onlineId === selectedOnlineId)
                }
                onClick={() => onLeafClick(leaf)}
                onDelete={leaf.kind === 'upload' && leaf.file ? () => onLeafDelete(leaf.file!) : undefined}
                isDeleting={leaf.kind === 'upload' && deletingUploadId === leaf.file?.upload_id}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SOURCE (level 1)
// =============================================================================

export function SourceGroup({
  source, expanded, onToggle, collapsedSections, onSectionToggle,
  selectedLeafId, selectedOnlineId, onLeafClick, onLeafDelete, deletingUploadId,
  extraContent,
}: {
  source: TreeSource;
  expanded: boolean;
  onToggle: () => void;
  collapsedSections: Set<string>;
  onSectionToggle: (sectionId: string) => void;
  selectedLeafId: string | null;
  selectedOnlineId: string | null;
  onLeafClick: (leaf: RefLeaf) => void;
  onLeafDelete: (file: DictUploadedFile) => void;
  deletingUploadId: string | null;
  /** Rich content rendered (when expanded) ABOVE the generic sections — used
   *  by "Other workspace" to host the consumed-spec SpecGroups in spec-mode. */
  extraContent?: React.ReactNode;
}) {
  return (
    <div className="mb-2 group">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-1.5 px-1 py-1 rounded border-b border-border-default hover:bg-bg-warm transition-colors text-left"
      >
        {expanded
          ? <ChevronDown size={12} className="text-fg-secondary flex-shrink-0" />
          : <ChevronRight size={12} className="text-fg-secondary flex-shrink-0" />}
        <span className="text-fg-secondary flex-shrink-0">{source.icon}</span>
        <span className="font-semibold text-fg-secondary uppercase tracking-wide text-[10px] truncate">{source.label}</span>
        <span
          className="ml-auto text-[10px] px-1 py-0.5 rounded-full bg-bg-warm text-fg-secondary flex-shrink-0"
          data-testid={`source-count-${source.id}`}
        >
          {source.count}
        </span>
      </button>
      {expanded && (
        <div className="mt-1 pl-1">
          {extraContent}
          {source.sections.map((section) => (
            <SectionGroup
              key={section.id}
              section={section}
              collapsed={collapsedSections.has(section.id)}
              onToggle={() => onSectionToggle(section.id)}
              selectedLeafId={selectedLeafId}
              selectedOnlineId={selectedOnlineId}
              onLeafClick={onLeafClick}
              onLeafDelete={onLeafDelete}
              deletingUploadId={deletingUploadId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// REGULATORY SOURCE (level 1 for pdf / eurlex / qa findings)
// =============================================================================

/** The "Regulatory reference" source: the findings grouped by document, each
 *  group's references in article order, the largest group first. `findings`
 *  are regulatory ones only (no concept, workspace or upload findings). */
export function regulatorySource(findings: Finding[]): TreeSource | null {
  const regGroups = new Map<string, Finding[]>();
  for (const f of findings) {
    const key = getRegulatoryGroupKey(f);
    const arr = regGroups.get(key) || [];
    arr.push(f);
    regGroups.set(key, arr);
  }
  if (regGroups.size === 0) return null;
  const sections: TreeSection[] = Array.from(regGroups.entries())
    .map(([key, groupFindings]) => {
      const sorted = [...groupFindings].sort((a, b) =>
        naturalSortKey(a.source_reference || '').localeCompare(naturalSortKey(b.source_reference || '')));
      const allNonFinal = sorted.length > 0 && sorted.every(isNonFinal);
      return {
        id: `reg:${key}`,
        title: getRegulatoryGroupLabel(key, sorted),
        statusBadge: allNonFinal ? (sorted[0].document_status || null) : null,
        leaves: sorted.map((f) => ({
          id: `finding:${f.finding_id}`,
          kind: 'finding' as LeafKind,
          title: findingLeafTitle(f),
          summary: findingLeafSummary(f),
          nonFinal: isNonFinal(f),
          finding: f,
        })),
      };
    })
    .sort((a, b) => (b.leaves.length - a.leaves.length) || a.title.localeCompare(b.title));
  return {
    id: 'regulatory',
    label: 'Regulatory reference',
    icon: <Scale size={12} strokeWidth={1.6} />,
    sections,
    count: sections.reduce((n, s) => n + s.leaves.length, 0),
  };
}

// =============================================================================
// LIST HEADER + EMPTY STATE
// =============================================================================

/** The list's header bar: icon, title, count and close. */
export function FindingsListHeader({
  title, count, onClose, headingId,
}: {
  title: string; count: number; onClose: () => void;
  /** Lets the surrounding region be labelled by the title. */
  headingId?: string;
}) {
  return (
    <div className="px-2 py-1.5 flex items-center justify-between border-b border-divider bg-bg-warm flex-shrink-0">
      <div className="flex items-center gap-1.5">
        <FileText size={14} className="text-accent" />
        <span id={headingId} className="text-xs font-semibold text-fg-secondary">{title}</span>
        <span className="text-[10px] px-1 py-0.5 rounded-full bg-accent-tint text-accent-hover">{count}</span>
      </div>
      <button onClick={onClose} className="p-1 hover:bg-bg-warm rounded" title="Close panel">
        <X size={14} className="text-fg-tertiary" />
      </button>
    </div>
  );
}

/** What the list says when it has nothing to show. */
export function FindingsEmptyState({ text }: { text: string }) {
  return (
    <div className="text-center py-12">
      <FileText size={32} className="mx-auto mb-3 text-fg-secondary" />
      <p className="text-sm text-fg-tertiary">{text}</p>
    </div>
  );
}
